<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { MenuDotsVerticalIcon } from '@solar-icons/vue/bold'
import AppMenu from '../../primitives/AppMenu/AppMenu.vue'
import AppAvatar from '../../primitives/AppAvatar/AppAvatar.vue'
import type { MenuItem } from '../../primitives/AppMenu/menu-types'
import type { NavUser } from './nav-types'

const props = withDefaults(
  defineProps<{
    user?: NavUser | null
  }>(),
  { user: null },
)

const emit = defineEmits<{
  'sign-out': []
}>()

const router = useRouter()
const { t } = useI18n()

// Locale-independent: never derive from the translated guest fallbacks below.
const FALLBACK_INITIALS = 'PL'

const displayName = computed(() => props.user?.name ?? t('shell.guestName'))
const displayEmail = computed(() => props.user?.email ?? t('shell.guestEmail'))
const initials = computed(() => {
  const user = props.user
  if (!user) return FALLBACK_INITIALS
  const parts = user.name.trim().split(/\s+/).filter(Boolean)
  const first = parts[0]?.[0] ?? ''
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : ''
  return (first + last).toUpperCase()
})

const buttonClass =
  'flex h-20 w-full items-center gap-4 rounded-4 px-2 text-left outline-none hover:bg-bg-tertiary focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent-blue'

const menuItems = computed<MenuItem[]>(() => [
  { id: 'settings', label: t('nav.settings') },
  { id: 'signout', label: t('common.signOut'), danger: true },
])

function onSelect(id: string) {
  if (id === 'signout') emit('sign-out')
  if (id === 'settings') void router.push('/settings/account')
}
</script>

<template>
  <AppMenu v-if="user" :items="menuItems" placement="top-start" @select="onSelect">
    <template #trigger="{ aria }">
      <button type="button" :aria-label="displayName" v-bind="aria" :class="buttonClass">
        <AppAvatar
          :name="displayName"
          :src="user.avatarUrl"
          :initials="initials"
          size="xs"
          tone="strong"
        />
        <span class="flex min-w-0 flex-1 flex-col">
          <span class="truncate text-caption">{{ displayName }}</span>
          <span class="truncate text-caption text-text-primary">{{ displayEmail }}</span>
        </span>
        <MenuDotsVerticalIcon class="shrink-0 text-icon-primary" aria-hidden="true" />
      </button>
    </template>
  </AppMenu>

  <button v-else type="button" :aria-label="displayName" :class="buttonClass">
    <AppAvatar :name="displayName" :initials="initials" size="xs" tone="strong" />
    <span class="flex min-w-0 flex-1 flex-col">
      <span class="truncate text-caption">{{ displayName }}</span>
      <span class="truncate text-caption text-text-primary">{{ displayEmail }}</span>
    </span>
    <MenuDotsVerticalIcon class="shrink-0 text-icon-primary" aria-hidden="true" />
  </button>
</template>
